import Button from "components/Button";
import { FC } from "react";

interface PaginationProps {
  currentPage: number;
  onPageChange: (page: number) => void;
  hasNextPage: boolean;
  // Optional so each page decides whether its clicks are tracked, and how
  onTrack?: (action: "previous_page" | "next_page" | "go_to_page") => void;
}

const Pagination: FC<PaginationProps> = ({
  currentPage,
  onPageChange,
  hasNextPage,
  onTrack,
}) => {
  const getVisiblePages = () => {
    const pages: (number | string)[] = [];

    if (currentPage === 1) {
      pages.push(1);
      // Only show next page if we know it has data
      if (hasNextPage) {
        pages.push(2);
      }
    } else {
      // For later pages, show: 1 ... prev current
      pages.push(1);
      if (currentPage > 3) {
        pages.push("...");
      }
      if (currentPage > 2) {
        pages.push(currentPage - 1);
      }
      pages.push(currentPage);
      // Only show next page if we know it has data
      if (hasNextPage) {
        pages.push(currentPage + 1);
      }
    }
    return pages;
  };

  const pages = getVisiblePages();

  return (
    <div className="flex justify-end items-center gap-1 mt-4 flex-wrap">
      <Button
        disabled={currentPage === 1}
        text="<"
        className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => {
          onPageChange(currentPage - 1);
          onTrack?.("previous_page");
        }}
      />

      {pages.map((page, index) =>
        page === "..." ? (
          <span key={`ellipsis-${index}`} className="px-2 text-gray-400">
            ...
          </span>
        ) : (
          <Button
            key={`page-${page}`}
            text={page?.toString()}
            className={`px-3 py-1 border rounded ${
              page === currentPage
                ? "border-blue-600 text-blue-600 font-semibold"
                : "hover:bg-gray-100"
            }`}
            onClick={() => {
              onPageChange(page as number);
              onTrack?.("go_to_page");
            }}
          />
        )
      )}

      <Button
        text=">"
        className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={!hasNextPage}
        onClick={() => {
          onPageChange(currentPage + 1);
          onTrack?.("next_page");
        }}
      />
    </div>
  );
};

export default Pagination;
